/*
 * Copyright (C) 2023 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { UserProviders } from '@administration-client/user';
import { ApiRootModule } from '@common/api-root-shared';
import { AppSharedModule } from '@alfa-client/app-shared';
import { EnvironmentModule } from '@common/environment-shared';
import { HintSharedModule } from '@alfa-client/hint-shared';
import { NavigationModule } from '@alfa-client/navigation';
import { OzgCloudUrlSerializer } from '@alfa-client/navigation-shared';
import { HttpBinaryFileInterceptor, HttpXsrfInterceptor, InjectorService, XhrInterceptor } from '@common/tech-shared';
import { BuildInfoComponent, HttpConnectionTimeoutInterceptor, HttpErrorInterceptor, SpinnerComponent } from '@ui';
import { VorgangSharedModule } from '@alfa-client/vorgang-shared';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import { LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { DateFnsAdapter } from '@angular/material-date-fns-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, UrlSerializer } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OAuthModule } from 'angular-oauth2-oidc';
import { provideAngularSvgIcon } from 'angular-svg-icon';
import { de } from 'date-fns/locale';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';

registerLocaleData(localeDe);

export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'dd.MM.yyyy',
  },
  display: {
    dateInput: 'dd.MM.yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'dd.MM.yyyy',
    monthYearA11yLabel: 'MM yyyy',
  },
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    RouterModule.forRoot(appRoutes, {
      initialNavigation: 'disabled',
      onSameUrlNavigation: 'reload',
    }),
    EnvironmentModule,
    OAuthModule.forRoot({
      resourceServer: {
        sendAccessToken: true,
      },
    }),
    ApiRootModule,
    NavigationModule,
    BuildInfoComponent,
    AppSharedModule,
    StoreModule.forRoot(
      {},
      {
        metaReducers: [],
        runtimeChecks: {
          strictActionImmutability: true,
          strictStateImmutability: true,
        },
      },
    ),
    EffectsModule.forRoot([]),
    environment.production ? [] : StoreDevtoolsModule.instrument({ connectInZone: true }),
    StoreRouterConnectingModule.forRoot(),
    // Workaround: load any module depending on ApiRootActions.loadApiRootSuccess
    // TODO Check if a future NgRx version will solve Lazy Loading + Effects.forFeature()
    HintSharedModule,
    SpinnerComponent,
    VorgangSharedModule,
  ],
  providers: [
    { provide: LOCALE_ID, useValue: 'de' },
    { provide: UrlSerializer, useClass: OzgCloudUrlSerializer },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConnectionTimeoutInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: XhrInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpXsrfInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpBinaryFileInterceptor,
      multi: true,
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: de,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: CUSTOM_DATE_FORMATS,
    },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    provideAppInitializer(() => {
      inject(InjectorService);
      return;
    }),
    UserProviders,
    provideHttpClient(withInterceptorsFromDi()),
    provideAngularSvgIcon(),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
